import React, { useState, useEffect, useRef } from 'react';
import { Chip, Tooltip } from '@mui/material';
import {
  Route as RouteIcon,
  ExpandMore as ExpandMoreIcon,
  Star as DefaultIcon
} from '@mui/icons-material';
import { useLayout } from '../../../context/LayoutContext';

const RouteSelector = ({
  routes = [],
  selectedRouteId,
  onRouteChange,
  isLoading = false,
  disabled = false,
  showAllOption = true,
  className = ''
}) => {
  const { theme } = useLayout();
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);
  const containerRef = useRef(null);

  // Encontrar ruta seleccionada
  const selectedRoute = routes.find(r => r.id === selectedRouteId);

  // Ordenar rutas: default primero, luego por posición
  const sortedRoutes = [...routes].sort((a, b) => {
    if (a.is_default && !b.is_default) return -1;
    if (!a.is_default && b.is_default) return 1;
    return a.position_order - b.position_order;
  });

  const handleToggle = () => {
    if (!disabled && !isLoading) {
      setIsOpen(!isOpen);
    }
  };

  const handleOptionClick = (value) => {
    if (onRouteChange) {
      onRouteChange(value === 'all' ? null : value);
    }
    setIsOpen(false);
  };

  // Cerrar dropdown al hacer click fuera
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Auto-seleccionar si solo hay una ruta activa
  useEffect(() => {
    const activeRoutes = routes.filter(r => r.is_active);
    if (activeRoutes.length === 1 && !selectedRouteId && !showAllOption) {
      onRouteChange(activeRoutes[0].id);
    }
  }, [routes, selectedRouteId, showAllOption, onRouteChange]);

  const renderSelectedValue = () => {
    if (!selectedRouteId || selectedRouteId === 'all') {
      return (
        <div className={`GM__${theme}-route-selector-content`}>
          <RouteIcon className={`GM__${theme}-route-selector-icon`} />
          <div className={`GM__${theme}-route-selector-text`}>
            <span className={`GM__${theme}-route-name`}>
              Todas las rutas
            </span>
          </div>
        </div>
      );
    }

    if (!selectedRoute) {
      return (
        <div className={`GM__${theme}-route-selector-content`}>
          <RouteIcon className={`GM__${theme}-route-selector-icon`} />
          <div className={`GM__${theme}-route-selector-text`}>
            <span className={`GM__${theme}-route-name`}>
              Seleccionar ruta
            </span>
          </div>
        </div>
      );
    }

    return (
      <div className={`GM__${theme}-route-selector-content`}>
        <RouteIcon className={`GM__${theme}-route-selector-icon`} />
        <div className={`GM__${theme}-route-selector-text`}>
          <span className={`GM__${theme}-route-name`}>
            {selectedRoute.route_name}
            {selectedRoute.is_default && (
              <Tooltip title="Ruta predeterminada">
                <DefaultIcon fontSize="small" style={{ marginLeft: 4 }} />
              </Tooltip>
            )}
          </span>
          <span className={`GM__${theme}-route-details`}>
            {selectedRoute.loading_point_name} → {selectedRoute.unloading_point_name}
          </span>
        </div>
        <div className={`GM__${theme}-route-selector-meta`}>
          <Chip
            label={selectedRoute.distance_km + ' km'}
            size="small"
            variant="outlined"
          />
        </div>
      </div>
    );
  };

  return (
    <div 
      ref={containerRef}
      className={`GM__${theme}-route-selector ${className}`}
    >
      <div className={`GM__${theme}-route-selector-container`}>
        <button
          className={`GM__${theme}-route-selector-button`}
          onClick={handleToggle}
          disabled={disabled || isLoading}
        >
          {renderSelectedValue()}
          
          <ExpandMoreIcon 
            className={`GM__${theme}-route-selector-arrow ${isOpen ? 'open' : ''}`}
          />
        </button>

        {isOpen && (
          <div 
            ref={dropdownRef}
            className={`GM__${theme}-route-selector-dropdown`}
          >
            {showAllOption && (
              <div
                className={`GM__${theme}-route-option ${(!selectedRouteId || selectedRouteId === 'all') ? 'selected' : ''}`}
                onClick={() => handleOptionClick('all')}
              >
                <div className={`GM__${theme}-route-option-content`}>
                  <RouteIcon className={`GM__${theme}-route-option-icon`} />
                  <div className={`GM__${theme}-route-option-info`}>
                    <span className={`GM__${theme}-route-option-name`}>
                      Todas las rutas
                    </span>
                  </div>
                </div>
              </div>
            )}

            {sortedRoutes.map((route) => (
              <div
                key={route.id}
                className={`GM__${theme}-route-option ${!route.is_active ? 'disabled' : ''} ${selectedRouteId === route.id ? 'selected' : ''}`}
                onClick={() => route.is_active && handleOptionClick(route.id)}
              >
                <div className={`GM__${theme}-route-option-content`}>
                  <RouteIcon className={`GM__${theme}-route-option-icon`} />
                  <div className={`GM__${theme}-route-option-info`}>
                    <span className={`GM__${theme}-route-option-name`}>
                      {route.route_name}
                      {route.is_default && (
                        <Tooltip title="Ruta predeterminada">
                          <DefaultIcon fontSize="small" />
                        </Tooltip>
                      )}
                    </span>
                    <span className={`GM__${theme}-route-option-details`}>
                      {route.loading_point_name} → {route.unloading_point_name}
                    </span>
                  </div>
                  <div className={`GM__${theme}-route-option-meta`}>
                    <Chip
                      label={route.client}
                      size="small"
                      variant="outlined"
                    />
                    <span className={`GM__${theme}-route-distance`}>
                      {route.distance_km} km
                    </span>
                  </div>
                </div>
              </div>
            ))}

            {routes.length === 0 && !isLoading && (
              <div className={`GM__${theme}-route-empty`}>
                No hay rutas disponibles
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default RouteSelector;